/**
 * Dream's Arts Evolution — 3D Product Preview Module
 * Uses Three.js to render customer designs on actual product shapes.
 *
 * Product types:
 * - mug: cylinder with handle cutout
 * - tumbler-skinny: tall narrow cylinder
 * - tumbler-wide: wider shorter cylinder
 * - shot-glass: small tapered cylinder
 * - beer-mug: cylinder with thick handle
 * - bottle: tall cylinder
 * - can-cooler: short wide cylinder
 * - phone-case: flat rectangular with rounded corners
 * - flat: flat rectangle (mouse pad, sign, license plate, patches, etc.)
 */
(function() {
  'use strict';

  const THREE_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';

  // Product shape definitions — dimensions are proportional
  const SHAPES = {
    'mug': { type: 'cylinder', radiusTop: 1, radiusBottom: 0.85, height: 2.4, segments: 64, color: '#ffffff', hasHandle: true },
    'mug-color-changing': { type: 'cylinder', radiusTop: 1, radiusBottom: 0.85, height: 2.4, segments: 64, color: '#222222', hasHandle: true },
    'mug-heart-handle': { type: 'cylinder', radiusTop: 1, radiusBottom: 0.85, height: 2.4, segments: 64, color: '#ffffff', hasHandle: true },
    'mug-with-spoon': { type: 'cylinder', radiusTop: 1, radiusBottom: 0.85, height: 2.4, segments: 64, color: '#ffffff', hasHandle: true },
    'stackable-mug': { type: 'cylinder', radiusTop: 0.85, radiusBottom: 0.85, height: 1.6, segments: 64, color: '#ffffff', hasHandle: false },
    'tumbler-skinny': { type: 'cylinder', radiusTop: 0.65, radiusBottom: 0.65, height: 4.2, segments: 64, color: '#e8e8e8', hasHandle: false },
    'tumbler-wide': { type: 'cylinder', radiusTop: 0.85, radiusBottom: 0.75, height: 3.5, segments: 64, color: '#e8e8e8', hasHandle: false },
    'tumbler-4in1': { type: 'cylinder', radiusTop: 0.9, radiusBottom: 0.9, height: 2.8, segments: 64, color: '#e0e0e0', hasHandle: false },
    'shot-glass': { type: 'cylinder', radiusTop: 0.6, radiusBottom: 0.4, height: 1.4, segments: 64, color: '#f0f0f0', hasHandle: false },
    'beer-mug': { type: 'cylinder', radiusTop: 0.95, radiusBottom: 0.8, height: 2.8, segments: 64, color: '#f5f5f5', hasHandle: true },
    'can-cooler': { type: 'cylinder', radiusTop: 0.85, radiusBottom: 0.85, height: 2.2, segments: 64, color: '#333333', hasHandle: false },
    'bottle-opener': { type: 'flat', width: 1.5, height: 5.5, color: '#c0c0c0', rounded: 0.3 },
    'phone-case': { type: 'phone', width: 2.2, height: 4.5, depth: 0.25, color: '#f0f0f0', rounded: 0.4 },
    'mouse-pad': { type: 'flat', width: 5, height: 4.2, color: '#222222', rounded: 0.2 },
    'license-plate': { type: 'flat', width: 6, height: 3, color: '#e0e0e0', rounded: 0.1 },
    'sign': { type: 'flat', width: 6, height: 4, color: '#d0d0d0', rounded: 0 },
    'rock-slate': { type: 'flat', width: 4, height: 3, color: '#444444', rounded: 0.05 },
    'coaster': { type: 'cylinder', radiusTop: 1.2, radiusBottom: 1.2, height: 0.15, segments: 64, color: '#f0f0f0', hasHandle: false },
    'passport': { type: 'flat', width: 4, height: 3, color: '#1a1a2e', rounded: 0.15 },
    'luggage-tag': { type: 'flat', width: 1.8, height: 3, color: '#8b4513', rounded: 0.2 },
    'patch': { type: 'flat', width: 2.5, height: 2.5, color: '#ffffff', rounded: 0.3 },
    'pin-button': { type: 'cylinder', radiusTop: 0.7, radiusBottom: 0.7, height: 0.08, segments: 64, color: '#ffffff', hasHandle: false },
    'fanny-pack': { type: 'flat', width: 5, height: 2.8, color: '#333333', rounded: 0.5 },
    'drawstring-bag': { type: 'flat', width: 4, height: 5.2, color: '#ffffff', rounded: 0.1 },
    'crossbody': { type: 'flat', width: 4, height: 4.5, color: '#333333', rounded: 0.6 },
    'wallet-short': { type: 'flat', width: 4, height: 3.5, color: '#222222', rounded: 0.2 },
    'wallet-long': { type: 'flat', width: 7, height: 3.5, color: '#222222', rounded: 0.2 },
    'socks': { type: 'flat', width: 2.5, height: 6, color: '#ffffff', rounded: 0.3 },
    'pen': { type: 'cylinder', radiusTop: 0.12, radiusBottom: 0.12, height: 5.5, segments: 32, color: '#ffffff', hasHandle: false },
    'cutting-board': { type: 'flat', width: 5.5, height: 4, color: '#f5f5f0', rounded: 0.3 },
    'photo-frame': { type: 'flat', width: 3.5, height: 4.5, color: '#e0e0e0', rounded: 0 },
    'puzzle': { type: 'flat', width: 6, height: 4.2, color: '#f5f5f5', rounded: 0 },
    'ornament': { type: 'cylinder', radiusTop: 1, radiusBottom: 1, height: 0.08, segments: 64, color: '#ffffff', hasHandle: false },
    'tote-bag': { type: 'flat', width: 5, height: 5, color: '#f0e8d8', rounded: 0.1 },
    'wristlet': { type: 'flat', width: 8, height: 0.8, color: '#333333', rounded: 0.3 },
    'badge-reel': { type: 'cylinder', radiusTop: 0.6, radiusBottom: 0.6, height: 0.15, segments: 64, color: '#333333', hasHandle: false },
  };

  // Map template IDs to shape keys
  const TEMPLATE_TO_SHAPE = {
    'mug-11oz': 'mug', 'mug-heart-handle': 'mug-heart-handle', 'mug-with-spoon': 'mug-with-spoon',
    'mug-color-changing': 'mug-color-changing', 'mug-15oz': 'mug', 'stackable-mug-6oz': 'stackable-mug',
    'tumbler-20oz-skinny': 'tumbler-skinny', 'tumbler-20oz-regular': 'tumbler-wide',
    'tumbler-32oz': 'tumbler-wide', 'tumbler-22oz': 'tumbler-wide', 'tumbler-16oz-4in1': 'tumbler-4in1',
    'tumbler-12oz-kids': 'tumbler-skinny',
    'shot-glass-1-5oz': 'shot-glass', 'beer-mug-glass-16oz': 'beer-mug',
    'can-cooler': 'can-cooler', 'bottle-opener': 'bottle-opener',
    'iphone-case-hardshell': 'phone-case', 'iphone-case-soft-rubber': 'phone-case',
    'samsung-case-soft': 'phone-case', 'samsung-case-hard': 'phone-case',
    'mouse-pad': 'mouse-pad', 'license-plate': 'license-plate',
    'aluminum-sign-8x12': 'sign', 'rock-slate-4x6': 'rock-slate', 'rock-slate-8x12': 'rock-slate',
    'coaster-round': 'coaster', 'coaster-square': 'coaster',
    'passport-cover': 'passport', 'luggage-tag': 'luggage-tag', 'luggage-handle-wrap': 'wristlet',
    'heat-patch-small': 'patch', 'heat-patch-medium': 'patch', 'heat-patch-large': 'patch',
    'pin-button-2-25': 'pin-button', 'pin-button-1-5': 'pin-button',
    'fanny-pack': 'fanny-pack', 'drawstring-bag': 'drawstring-bag',
    'round-crossbody': 'crossbody', 'crossbody-messenger': 'crossbody',
    'neoprene-wristlet': 'wristlet', 'socks-sublimation': 'socks', 'sublimation-pen': 'pen',
    'cleaning-cloth': 'drawstring-bag',
    'keychain-rectangle': 'luggage-tag', 'keychain-round': 'pin-button',
    'puzzle-rectangle': 'puzzle', 'puzzle-heart': 'puzzle',
    'ornament-circle': 'ornament', 'ornament-star': 'ornament',
  };

  let threeLoaded = false;

  function loadThree() {
    return new Promise((resolve, reject) => {
      if (typeof THREE !== 'undefined') { threeLoaded = true; resolve(); return; }
      const s = document.createElement('script');
      s.src = THREE_CDN;
      s.onload = () => { threeLoaded = true; resolve(); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function getShapeForTemplate(templateId) {
    const shapeKey = TEMPLATE_TO_SHAPE[templateId] || 'mug';
    return SHAPES[shapeKey] || SHAPES['mug'];
  }

  /**
   * Open 3D preview modal
   * @param {string} templateId - template key from customizer_templates.json
   * @param {HTMLCanvasElement|string} designCanvas - Fabric.js canvas element or base64 image
   * @param {string} productTitle - display title
   */
  async function showPreview(templateId, designCanvas, productTitle) {
    await loadThree();

    const shape = getShapeForTemplate(templateId);

    // Get design as texture
    let textureData;
    if (typeof designCanvas === 'string') {
      textureData = designCanvas; // already base64
    } else if (designCanvas.toDataURL) {
      textureData = designCanvas.toDataURL('image/png');
    } else {
      console.error('[3D Preview] Invalid design source');
      return;
    }

    // Create modal
    const modal = document.createElement('div');
    modal.id = 'da-3d-modal';
    modal.innerHTML = `
      <style>
        #da-3d-modal{position:fixed;top:0;left:0;right:0;bottom:0;z-index:200000;background:rgba(0,0,0,.85);display:flex;flex-direction:column;align-items:center;justify-content:center;font-family:-apple-system,sans-serif}
        #da-3d-modal .da-3d-header{color:#fff;font-size:18px;font-weight:700;margin-bottom:12px;text-align:center}
        #da-3d-modal .da-3d-sub{color:#aaa;font-size:13px;margin-bottom:16px;text-align:center}
        #da-3d-modal canvas{border-radius:16px;box-shadow:0 20px 60px rgba(0,0,0,.5);cursor:grab}
        #da-3d-modal canvas:active{cursor:grabbing}
        #da-3d-modal .da-3d-controls{display:flex;gap:12px;margin-top:20px}
        #da-3d-modal .da-3d-btn{padding:12px 28px;border-radius:10px;border:none;font-weight:700;font-size:14px;cursor:pointer;transition:all .2s}
        #da-3d-modal .da-3d-close{background:#dc1e1e;color:#fff}
        #da-3d-modal .da-3d-close:hover{background:#ff3333;transform:translateY(-2px)}
        #da-3d-modal .da-3d-rotate{background:rgba(255,255,255,.1);color:#fff;border:1px solid rgba(255,255,255,.2)}
        #da-3d-modal .da-3d-rotate:hover{background:rgba(255,255,255,.2)}
        #da-3d-hint{color:#666;font-size:12px;margin-top:10px}
      </style>
      <div class="da-3d-header">${productTitle || 'Product Preview'}</div>
      <div class="da-3d-sub">Drag to rotate • Scroll to zoom</div>
      <div id="da-3d-container"></div>
      <div class="da-3d-controls">
        <button class="da-3d-btn da-3d-rotate" id="da3dAutoRotate">⟳ Auto Rotate</button>
        <button class="da-3d-btn da-3d-close" id="da3dClose">✕ Close Preview</button>
      </div>
      <div id="da-3d-hint">Arrastra para rotar • Scroll para zoom</div>
    `;
    document.body.appendChild(modal);

    const container = document.getElementById('da-3d-container');
    const W = Math.min(window.innerWidth * 0.8, 700);
    const H = Math.min(window.innerHeight * 0.6, 500);

    // Setup Three.js
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, W / H, 0.1, 100);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(3, 5, 4);
    scene.add(dirLight);
    const backLight = new THREE.DirectionalLight(0xffffff, 0.3);
    backLight.position.set(-2, 3, -3);
    scene.add(backLight);

    // Load design texture
    const textureLoader = new THREE.TextureLoader();
    const texture = textureLoader.load(textureData);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;

    let productMesh;

    if (shape.type === 'cylinder') {
      // Cylindrical products (mugs, tumblers, glasses, etc.)
      const geo = new THREE.CylinderGeometry(
        shape.radiusTop, shape.radiusBottom, shape.height, shape.segments, 1, true
      );
      const mat = new THREE.MeshPhongMaterial({
        map: texture,
        side: THREE.DoubleSide,
        shininess: 60,
      });
      productMesh = new THREE.Mesh(geo, mat);

      // Add top/bottom caps
      const capGeo = new THREE.CircleGeometry(shape.radiusTop, shape.segments);
      const capMat = new THREE.MeshPhongMaterial({ color: shape.color, shininess: 40 });
      const topCap = new THREE.Mesh(capGeo, capMat);
      topCap.position.y = shape.height / 2;
      topCap.rotation.x = -Math.PI / 2;
      productMesh.add(topCap);

      const botCapGeo = new THREE.CircleGeometry(shape.radiusBottom, shape.segments);
      const botCap = new THREE.Mesh(botCapGeo, capMat);
      botCap.position.y = -shape.height / 2;
      botCap.rotation.x = Math.PI / 2;
      productMesh.add(botCap);

      // Add handle for mugs
      if (shape.hasHandle) {
        const handleCurve = new THREE.CatmullRomCurve3([
          new THREE.Vector3(shape.radiusTop * 0.95, shape.height * 0.3, 0),
          new THREE.Vector3(shape.radiusTop * 1.5, shape.height * 0.2, 0),
          new THREE.Vector3(shape.radiusTop * 1.55, 0, 0),
          new THREE.Vector3(shape.radiusTop * 1.5, -shape.height * 0.2, 0),
          new THREE.Vector3(shape.radiusBottom * 0.95, -shape.height * 0.3, 0),
        ]);
        const handleGeo = new THREE.TubeGeometry(handleCurve, 20, 0.08, 8, false);
        const handleMat = new THREE.MeshPhongMaterial({ color: shape.color, shininess: 40 });
        const handle = new THREE.Mesh(handleGeo, handleMat);
        productMesh.add(handle);
      }

      camera.position.set(0, shape.height * 0.3, shape.height * 1.8);

    } else if (shape.type === 'phone') {
      // Phone case — rounded rectangle
      const phoneShape = new THREE.Shape();
      const w = shape.width / 2, h = shape.height / 2, r = shape.rounded;
      phoneShape.moveTo(-w + r, -h);
      phoneShape.lineTo(w - r, -h);
      phoneShape.quadraticCurveTo(w, -h, w, -h + r);
      phoneShape.lineTo(w, h - r);
      phoneShape.quadraticCurveTo(w, h, w - r, h);
      phoneShape.lineTo(-w + r, h);
      phoneShape.quadraticCurveTo(-w, h, -w, h - r);
      phoneShape.lineTo(-w, -h + r);
      phoneShape.quadraticCurveTo(-w, -h, -w + r, -h);

      const extrudeSettings = { depth: shape.depth, bevelEnabled: true, bevelThickness: 0.03, bevelSize: 0.03, bevelSegments: 3 };
      const geo = new THREE.ExtrudeGeometry(phoneShape, extrudeSettings);

      // Front face gets the texture, sides get solid color
      const materials = [
        new THREE.MeshPhongMaterial({ map: texture, shininess: 30 }),
        new THREE.MeshPhongMaterial({ color: shape.color, shininess: 30 }),
      ];
      // Assign material indices
      geo.faces?.forEach(face => { face.materialIndex = face.normal.z > 0.5 ? 0 : 1; });

      const mat = new THREE.MeshPhongMaterial({ map: texture, shininess: 40 });
      productMesh = new THREE.Mesh(geo, mat);
      camera.position.set(0, 0, 6);

    } else {
      // Flat products — simple plane with slight depth
      const geo = new THREE.BoxGeometry(shape.width, shape.height, 0.08);
      const mat = new THREE.MeshPhongMaterial({ map: texture, shininess: 20 });
      productMesh = new THREE.Mesh(geo, mat);

      // Add back face with product color
      const backGeo = new THREE.BoxGeometry(shape.width, shape.height, 0.02);
      const backMat = new THREE.MeshPhongMaterial({ color: shape.color });
      const back = new THREE.Mesh(backGeo, backMat);
      back.position.z = -0.05;
      productMesh.add(back);

      camera.position.set(0, 0, Math.max(shape.width, shape.height) * 1.3);
    }

    scene.add(productMesh);
    camera.lookAt(0, 0, 0);

    // Mouse/touch controls
    let isDragging = false;
    let prevX = 0, prevY = 0;
    let rotX = 0, rotY = 0;
    let autoRotate = true;
    let zoom = 1;

    renderer.domElement.addEventListener('mousedown', e => { isDragging = true; prevX = e.clientX; prevY = e.clientY; });
    renderer.domElement.addEventListener('touchstart', e => { isDragging = true; prevX = e.touches[0].clientX; prevY = e.touches[0].clientY; });

    window.addEventListener('mousemove', e => {
      if (!isDragging) return;
      autoRotate = false;
      rotY += (e.clientX - prevX) * 0.008;
      rotX += (e.clientY - prevY) * 0.008;
      rotX = Math.max(-1, Math.min(1, rotX));
      prevX = e.clientX; prevY = e.clientY;
    });
    window.addEventListener('touchmove', e => {
      if (!isDragging) return;
      autoRotate = false;
      rotY += (e.touches[0].clientX - prevX) * 0.008;
      rotX += (e.touches[0].clientY - prevY) * 0.008;
      rotX = Math.max(-1, Math.min(1, rotX));
      prevX = e.touches[0].clientX; prevY = e.touches[0].clientY;
    });
    window.addEventListener('mouseup', () => isDragging = false);
    window.addEventListener('touchend', () => isDragging = false);

    renderer.domElement.addEventListener('wheel', e => {
      e.preventDefault();
      zoom = Math.max(0.5, Math.min(2, zoom + e.deltaY * -0.001));
    });

    // Animation loop
    let animId;
    function animate() {
      animId = requestAnimationFrame(animate);
      if (autoRotate) rotY += 0.008;
      productMesh.rotation.y = rotY;
      productMesh.rotation.x = rotX;
      camera.zoom = zoom;
      camera.updateProjectionMatrix();
      renderer.render(scene, camera);
    }
    animate();

    // Auto rotate toggle
    document.getElementById('da3dAutoRotate').addEventListener('click', () => {
      autoRotate = !autoRotate;
      document.getElementById('da3dAutoRotate').textContent = autoRotate ? '⏸ Stop Rotation' : '⟳ Auto Rotate';
    });

    // Close
    function close3D() {
      cancelAnimationFrame(animId);
      renderer.dispose();
      modal.remove();
    }
    document.getElementById('da3dClose').addEventListener('click', close3D);
    modal.addEventListener('click', e => { if (e.target === modal) close3D(); });
  }

  // Public API
  window.DreamsArts3DPreview = {
    show: showPreview,
    getShapes: () => SHAPES,
    getShapeForTemplate: getShapeForTemplate,
    TEMPLATE_TO_SHAPE,
  };

})();
